import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Dropdown, Grid, Icon, Popup } from 'semantic-ui-react'
import { createEntity, deleteEntity, updateEntity } from 'actions/entities'
import { findEntity } from 'selectors/entities'

const NAMES_TOKEN = '@@NAMES@@'

const recipientKey = (type, id) => `${type}:${String(id)}`

const hasEmail = (ref) => Boolean(ref && ref.email)

const activeRecipients = (queryset) =>
  queryset ? queryset.toRefArray().filter((r) => r.deleted !== "1") : []

const allRecipients = (queryset) =>
  queryset ? queryset.toRefArray() : []

const contactName = (ref) => `${ref.firstName || ''} ${ref.lastName || ''}`.trim()

// Candidates = the patient + each relationship's contact/organisation, deduped by key.
const buildCandidates = (dossier) => {
  const byKey = new Map()
  const add = (type, ref, name, relationshipType) => {
    const key = recipientKey(type, ref.id)
    if (!byKey.has(key)) byKey.set(key, { key, type, id: String(ref.id), name, hasEmail: hasEmail(ref), relationshipType })
  }

  // Patient added first so it wins the dedup and is never labelled as a relationship.
  if (dossier.patient) add('Contact', dossier.patient.ref, contactName(dossier.patient.ref))

  dossier.relationships.toModelArray()
    .filter((rel) => rel.ref.deleted !== "1")
    .forEach((rel) => {
      if (rel.contact) add('Contact', rel.contact.ref, contactName(rel.contact.ref), rel.ref.relationshipType)
      else if (rel.organisation) add('Organisation', rel.organisation.ref, rel.organisation.ref.name, rel.ref.relationshipType)
    })

  return Array.from(byKey.values())
}

const noEmailWarning = () =>
  <Popup
    trigger={<Icon name='warning sign' color='orange' style={{ marginLeft: '0.25em' }}/>}
    content={I18n.t('components.communication_recipients.no_email')}
  />

// A recipient's descriptor: the patient reads "de patiënt zelf"; a relationship reads
// "de <role> <name>" (e.g. "de ouder Homer Simpson"); anything else falls back to the name.
const recipientDescriptor = (recipient, candidateByKey, patientKey) => {
  const key = recipientKey(recipient.recipientType, recipient.recipientId)

  const candidate = candidateByKey[key]
  const emailPresent = candidate ? candidate.hasEmail : recipient.hasEmail
  const name = candidate ? candidate.name : recipient.name
  const warning = emailPresent === false ? noEmailWarning() : null

  if (key === patientKey) {
    return <React.Fragment>{I18n.t('components.communication_recipients.default_target')}{warning}</React.Fragment>
  }

  if (candidate && candidate.relationshipType) {
    const role = I18n.t(`api.forms.relationship.options.${candidate.relationshipType}`).toLowerCase()
    return <React.Fragment>{I18n.t('components.communication_recipients.role', { role })} {name}{warning}</React.Fragment>
  }

  return <React.Fragment>{name}{warning}</React.Fragment>
}

// Renders the recipient descriptors with Dutch list grammar ("X", "X en Y", "X, Y en Z").
const renderRecipients = (recipients, candidateByKey, patientKey) =>
  recipients.map((recipient, index) => {
    let connector = ''
    if (index > 0) {
      connector = index === recipients.length - 1 ? ' en ' : ', '
    }
    return (
      <React.Fragment key={recipient.id}>
        {connector}
        {recipientDescriptor(recipient, candidateByKey, patientKey)}
      </React.Fragment>
    )
  })

// The recipient clause of the sentence ("de patiënt zelf" / "de ouder X en de cliënt zelf").
const targetNode = (recipients, candidateByKey, patientKey) => {
  if (recipients.length === 0) {
    return I18n.t('components.communication_recipients.default_target')
  }
  const [before, after] = I18n.t('components.communication_recipients.to_target', { names: NAMES_TOKEN }).split(NAMES_TOKEN)
  return <React.Fragment>{before}{renderRecipients(recipients, candidateByKey, patientKey)}{after}</React.Fragment>
}

const sentenceStyle = { color: 'rgba(0,0,0,0.6)' }

const CommunicationRecipientsField = (props) => {
  const { recipients, allRecipientRefs, candidates, removedRelationshipKeys, patientKey, dossierId,
          createRecipient, softDelete, undelete } = props
  const [editing, setEditing] = useState(false)

  const activeKeys = recipients.map((r) => recipientKey(r.recipientType, r.recipientId))
  const selectedKeys = activeKeys.length ? activeKeys : (patientKey ? [patientKey] : [])

  // No-email candidates stay selectable; the warning shows in the sentence, not in the picker.
  const options = candidates.map((c) => ({ key: c.key, value: c.key, text: c.name }))

  const candidateByKey = {}
  candidates.forEach((c) => { candidateByKey[c.key] = c })

  // When a relationship is removed, its recipient no longer maps to a candidate; drop it. Guard on
  // the relationship being explicitly removed (present but soft-deleted) so a not-yet-loaded
  // contact can never be mistaken for an orphan and wrongly deleted. The backend also cleans these
  // up on save; this just keeps the live editor consistent.
  const orphanIds = recipients
    .filter((r) => {
      const key = recipientKey(r.recipientType, r.recipientId)
      return !candidateByKey[key] && removedRelationshipKeys.includes(key)
    })
    .map((r) => r.id)

  useEffect(() => {
    orphanIds.forEach((id) => softDelete(id))
  }, [orphanIds.join(',')])

  const applyDesired = (desiredKeys) => {
    // Canonicalization: the patient alone is the default => store no recipients.
    const desired = (desiredKeys.length === 1 && desiredKeys[0] === patientKey) ? [] : desiredKeys

    const byKey = {}
    allRecipientRefs.forEach((r) => { byKey[recipientKey(r.recipientType, r.recipientId)] = r })

    desired.forEach((key) => {
      const existing = byKey[key]
      if (existing) {
        if (existing.deleted === "1") undelete(existing.id)
      } else {
        const c = candidateByKey[key]
        if (c) createRecipient({ dossierId, recipientType: c.type, recipientId: c.id, name: c.name, hasEmail: c.hasEmail })
      }
    })

    allRecipientRefs
      .filter((r) => r.deleted !== "1")
      .filter((r) => !desired.includes(recipientKey(r.recipientType, r.recipientId)))
      .forEach((r) => softDelete(r.id))
  }

  const handleChange = (event, { value }) => {
    if (!value || value.length === 0) return // at least one communication recipient is required
    applyDesired(value)
  }

  return (
    <Grid style={{ marginTop: 0, marginBottom: 0 }}>
      <Grid.Row style={{ paddingTop: '0.25em', paddingBottom: '0.25em' }}>
        <Grid.Column width={1}/>
        <Grid.Column width={15}>
          <span style={sentenceStyle}>
            {I18n.t('components.communication_recipients.lead')}{' '}
            {editing
              ? <Dropdown
                  multiple selection defaultOpen
                  options={options}
                  value={selectedKeys}
                  onChange={handleChange}
                  onClose={() => setEditing(false)}
                  onBlur={() => setEditing(false)}
                />
              : <React.Fragment>
                  {targetNode(recipients, candidateByKey, patientKey)}{'.'}
                      <Icon
                        name='pencil' link color='grey' style={{ marginLeft: '0.5em' }}
                        onClick={() => setEditing(true)}
                      />

                </React.Fragment>}
          </span>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

const mapStateToProps = (state, ownProps) => {
  const dossier = findEntity(state, ownProps.formContext.entityName, ownProps.formContext.entityId)
  if (!dossier) {
    return { recipients: [], allRecipientRefs: [], candidates: [], removedRelationshipKeys: [], patientKey: null, dossierId: ownProps.formContext.entityId }
  }

  const removedRelationshipKeys = dossier.relationships.toRefArray()
    .filter((r) => r.deleted === "1")
    .map((r) => (r.contactId ? recipientKey('Contact', r.contactId) : recipientKey('Organisation', r.organisationId)))

  const candidates = buildCandidates(dossier)
  return {
    recipients: activeRecipients(dossier.communicationRecipients),
    allRecipientRefs: allRecipients(dossier.communicationRecipients),
    candidates,
    removedRelationshipKeys,
    patientKey: dossier.patient ? recipientKey('Contact', dossier.patient.ref.id) : null,
    dossierId: ownProps.formContext.entityId
  }
}

const mapDispatchToProps = (dispatch) => ({
  createRecipient: (fields) => dispatch(createEntity('communicationRecipient', fields)),
  softDelete: (id) => dispatch(deleteEntity('communicationRecipient', id)),
  undelete: (id) => dispatch(updateEntity('communicationRecipient', { id, deleted: "0" }))
})

export default connect(mapStateToProps, mapDispatchToProps)(CommunicationRecipientsField)
